import { z } from 'zod';
import { DeviceKind, Instant, PageRequest, pageOf, Uuid } from '../common';
import { FilterFacet } from '../facets';
import { DeviceCommandArgs, DeviceCommandKind, DeviceCommandRecord } from '../handheld-commands';
import { HandheldConfigLayer, HandheldConfigPatch } from '../handheld-config';
import { DiagnosticObject, DiagnosticObjectName, HandheldAdoption } from '../handheld-diagnostics';
import {
  HandheldCanaryLocations,
  HandheldRelease,
  HandheldSigningKeyId,
  HandheldUpdateState,
  HandheldVersionCodeParam,
} from '../handheld-release';
import { HandheldTelemetry } from '../handheld-telemetry';
import { defineEndpoint } from '../http';
import { patchSchema } from '../patch';
import type { CounterReadiness as CounterReadinessValue } from '../projections/counter-readiness';
import { CounterReadinessSchema } from '../projections/counter-readiness';

// ---------------------------------------------------------------------------
// The counter fleet's release channel (docs/plans/handheld-fleet.html 2)
// ---------------------------------------------------------------------------

/**
 * Everything the Releases screen reads in one call: the singleton the offer is
 * computed from, and every build the platform holds, newest first.
 */
export const listHandheldReleases = defineEndpoint({
  method: 'GET',
  path: '/internal/handheld/releases',
  mount: 'internal',
  summary: 'Every uploaded counter build and the current update state (OPS-029)',
  response: z.strictObject({
    update: HandheldUpdateState,
    releases: z.array(HandheldRelease),
  }),
});

export const getHandheldRelease = defineEndpoint({
  method: 'GET',
  path: '/internal/handheld/releases/:versionCode',
  mount: 'internal',
  summary: 'One uploaded counter build, with its signature if it has one (OPS-029)',
  params: HandheldVersionCodeParam,
  response: HandheldRelease,
});

/**
 * Uploading a build.
 *
 * Base64 in a JSON body rather than multipart, for the reason `uploadMedia`
 * gives: multipart would have to bypass `bindEndpoint` entirely, and an
 * unvalidated upload endpoint on the one route that puts executable code in
 * front of a counter is not a trade anybody should make.
 *
 * `sha256` and `sizeBytes` are deliberately NOT in this body. The worker
 * computes both from the bytes it decoded; a hash the uploader states is a hash
 * that says whatever the uploader was told to type, and the founder signs what
 * the PLATFORM computed.
 */
export const uploadHandheldRelease = defineEndpoint({
  method: 'POST',
  path: '/internal/handheld/releases',
  mount: 'internal',
  summary: 'Store a counter build and record what the platform computed from it (OPS-029)',
  body: z.strictObject({
    versionCode: z.number().int().min(1).max(2_100_000_000),
    versionName: z.string().min(1).max(40),
    /** Base64, no data-URI prefix. The cap is ~18 MB of APK. */
    data: z.string().min(16).max(24_000_000),
    contentType: z.literal('application/vnd.android.package-archive'),
  }),
  idempotent: false,
  response: HandheldRelease,
});

/**
 * The exact bytes the founder is asked to sign (plan 6.4, 16.1).
 *
 * The platform PROPOSES the document rather than leaving an operator to compose
 * it: every field in it is a fact the platform computed - the hash and the size
 * off the stored bytes, the channel off this worker's own environment - and a
 * manifest typed by hand is a manifest whose `sha256` says whatever somebody was
 * told to type. It is only a proposal: the Sign dialog leaves the box editable,
 * and `signHandheldRelease` verifies the signature against whatever string it is
 * given, so a founder who regenerated the file offline pastes theirs.
 *
 * A STRING and not the parsed object, deliberately. The signature covers bytes,
 * so key order and the absence of whitespace are part of the artifact; handing
 * back an object would let every client re-serialize it differently and every
 * one of those differences is a signature that fails at a bakery.
 */
export const getHandheldReleaseManifest = defineEndpoint({
  method: 'GET',
  path: '/internal/handheld/releases/:versionCode/manifest',
  mount: 'internal',
  summary: 'The exact manifest bytes to sign offline for one build (OPS-029)',
  params: HandheldVersionCodeParam,
  response: z.strictObject({ manifestJson: z.string().min(2).max(2048) }),
});

/**
 * Attaching the offline signature - a separate act, by a separate person (plan
 * 16.1).
 *
 * One compromised operator account can upload a build and publish it; every
 * device will discard it, because the manifest was never signed by a key the
 * platform has ever held. That property only survives while this stays its own
 * step: fold it into the upload and the signing becomes something the uploading
 * session does.
 */
export const signHandheldRelease = defineEndpoint({
  method: 'POST',
  path: '/internal/handheld/releases/:versionCode/signature',
  mount: 'internal',
  summary: 'Record the offline signature over a build manifest (OPS-029)',
  params: HandheldVersionCodeParam,
  body: z.strictObject({
    /** The EXACT bytes that were signed, kept verbatim and never re-serialized. */
    manifestJson: z.string().min(2).max(2048),
    signature: z.string().min(64).max(1024),
    keyId: HandheldSigningKeyId,
  }),
  idempotent: false,
  response: HandheldRelease,
});

/**
 * Publishing: the build becomes the latest, offered to ring 0 and nobody else.
 *
 * A publish resets the rollout to the administered canary shops at 0 percent,
 * so reaching the fleet is always a second, deliberate act (plan 12).
 */
export const publishHandheldRelease = defineEndpoint({
  method: 'POST',
  path: '/internal/handheld/releases/:versionCode/publish',
  mount: 'internal',
  summary: 'Make a signed build the latest release, offered to the canary ring (OPS-029)',
  params: HandheldVersionCodeParam,
  idempotent: false,
  response: z.strictObject({ update: HandheldUpdateState, release: HandheldRelease }),
});

/**
 * Forcing: raising the minimum, which is the whole mechanism (plan 2, 8).
 *
 * `confirmVersionCode` is the typed confirmation the platform already uses for
 * a permanent delete, and it is here because a force is an outage-shaped action
 * operated from an office chair (plan 16.5). A grace of 0 blocks the fleet now,
 * and it is a number somebody typed rather than a default.
 */
export const setHandheldForce = defineEndpoint({
  method: 'POST',
  path: '/internal/handheld/force',
  mount: 'internal',
  summary: 'Raise the minimum counter build, with a stated grace window (OPS-029)',
  body: z.strictObject({
    versionCode: z.number().int().min(1).max(2_100_000_000),
    /** Up to a week. 0 is an emergency, and it is typed rather than defaulted. */
    graceMinutes: z.number().int().min(0).max(10_080),
    confirmVersionCode: z.number().int().min(1).max(2_100_000_000),
  }),
  idempotent: false,
  response: HandheldUpdateState,
});

export const clearHandheldForce = defineEndpoint({
  method: 'DELETE',
  path: '/internal/handheld/force',
  mount: 'internal',
  summary: 'Remove the forced minimum so nothing is blocked again (OPS-029)',
  idempotent: false,
  response: HandheldUpdateState,
});

/**
 * The canary shops, administered rather than hardcoded: which counters take a
 * new build first is an operational choice that changes as the estate does, and
 * a publish reads this list to build ring 0 from.
 */
/**
 * Widening the ring (plan 12).
 *
 * ONE number, and it only ever goes up. Ring 0 is the canary shops a publish
 * resets to; ring 1 is this percentage of everything else, decided by a hash of
 * the device id so a counter does not flicker in and out of the offer between
 * beats; ring 2 is 100. The refusal to LOWER it is the whole contract of the
 * endpoint: taking a build back off a device is not something this platform can
 * do - Android does not go backwards, and the answer to a bad build is a halt
 * and the next build.
 */
export const widenHandheldRollout = defineEndpoint({
  method: 'POST',
  path: '/internal/handheld/rollout',
  mount: 'internal',
  summary: 'Widen the rollout of the latest published counter build (OPS-029)',
  body: z.strictObject({ percent: z.number().int().min(0).max(100) }),
  idempotent: false,
  response: HandheldUpdateState,
});

/**
 * Halting a rollout (plan 12).
 *
 * A halt stops the OFFER. Devices that have already installed the build keep
 * it, because Android does not go backwards; devices that have not are offered
 * the previous published build instead, so the fleet is never left with nothing
 * on offer while somebody prepares the next one. The version number is typed
 * back for the same reason a force is: stopping a rollout is an operational act
 * with a fleet-sized blast radius.
 */
export const haltHandheldRelease = defineEndpoint({
  method: 'POST',
  path: '/internal/handheld/releases/:versionCode/halt',
  mount: 'internal',
  summary: 'Stop offering a build to counters that have not installed it (OPS-029)',
  params: HandheldVersionCodeParam,
  body: z.strictObject({ confirmVersionCode: z.number().int().min(1).max(2_100_000_000) }),
  idempotent: false,
  response: z.strictObject({ update: HandheldUpdateState, release: HandheldRelease }),
});

/**
 * Un-halting, by an operator who has looked (plan 12).
 *
 * It does NOT put the build back on offer by itself, and that is the point: an
 * automatic halt at 02:00 is a machine's opinion, and reversing it is a
 * decision plus a publish. Clearing `haltedAt` makes the build publishable
 * again; publishing it restarts the rollout at ring 0, where a build that has
 * already hurt ten counters belongs.
 */
export const unhaltHandheldRelease = defineEndpoint({
  method: 'DELETE',
  path: '/internal/handheld/releases/:versionCode/halt',
  mount: 'internal',
  summary: 'Clear a halt so a build can be published again (OPS-029)',
  params: HandheldVersionCodeParam,
  idempotent: false,
  response: z.strictObject({ update: HandheldUpdateState, release: HandheldRelease }),
});

/**
 * How far the current build has actually got (plan 12).
 *
 * Read from the same estate the fleet list reads, in the same two queries, and
 * counted from what the DEVICES report rather than from what the platform
 * offered. The two are not the same number during a rollout, and the difference
 * is the entire reason this panel exists.
 */
export const getHandheldAdoption = defineEndpoint({
  method: 'GET',
  path: '/internal/handheld/adoption',
  mount: 'internal',
  summary: 'Counters per build, with the failed installs named (OPS-029)',
  response: HandheldAdoption,
});

export const getHandheldCanaryLocations = defineEndpoint({
  method: 'GET',
  path: '/internal/handheld/canary-locations',
  mount: 'internal',
  summary: 'Which shops take a new counter build first (OPS-029)',
  response: HandheldCanaryLocations,
});

export const setHandheldCanaryLocations = defineEndpoint({
  method: 'PUT',
  path: '/internal/handheld/canary-locations',
  mount: 'internal',
  summary: 'Choose which shops take a new counter build first (OPS-029)',
  body: HandheldCanaryLocations,
  idempotent: false,
  response: HandheldCanaryLocations,
});

// ---------------------------------------------------------------------------
// The counter fleet's alert thresholds (docs/plans/handheld-fleet.html 10)
// ---------------------------------------------------------------------------

/**
 * Every number the nine telemetry alerts are judged against (plan 10).
 *
 * Administered rather than compiled in, on the monitoring-thresholds precedent
 * beside it and for the same reason: the figure that means "something is wrong"
 * is learned from a fleet, not chosen before there is one, and moving it must
 * not be a deploy. The defaults ARE the plan's - they are stated once, in the
 * worker's `state.ts`, and nothing here restates them.
 *
 * Two of them come in PAIRS, and the pair is the point. A single line for the
 * clock skew or for free storage would raise, clear and raise again on a device
 * sitting exactly on it - the flapping alert nobody reads by Thursday - so the
 * raise and the clear are separate numbers with a gap between them.
 */
export const HandheldThresholds = z.object({
  /** Under this, and not charging, inside the service window: DEVICE_BATTERY_LOW. */
  batteryLowPercent: z.int().min(1).max(99),
  /** |skew| over this on two consecutive beats: DEVICE_CLOCK_SKEW. */
  clockSkewRaiseMillis: z.int().min(1_000).max(86_400_000),
  /** ...and it clears under this. Below the raise, or the pair does nothing. */
  clockSkewResolveMillis: z.int().min(0).max(86_400_000),
  /** Free megabytes under which a staged APK cannot be written: DEVICE_STORAGE_LOW. */
  storageLowMb: z.int().min(1).max(1_000_000),
  /** ...and it clears over this. Above the raise, or the pair does nothing. */
  storageResolveMb: z.int().min(1).max(1_000_000),
  /** Crashes inside the window below that add up to a loop. */
  crashLoopCount: z.int().min(1).max(1000),
  crashLoopWindowMinutes: z.int().min(1).max(1440),
  /** A build staged this long without the version changing: DEVICE_UPDATE_FAILED. */
  updateStalledHours: z.int().min(1).max(8760),
});
export type HandheldThresholds = z.infer<typeof HandheldThresholds>;

export const getHandheldThresholds = defineEndpoint({
  method: 'GET',
  path: '/internal/handheld/thresholds',
  mount: 'internal',
  summary: 'The administered thresholds the counter-fleet alerts are judged against (OPS-029)',
  response: HandheldThresholds,
});

export const setHandheldThresholds = defineEndpoint({
  method: 'PUT',
  path: '/internal/handheld/thresholds',
  mount: 'internal',
  // PATCH semantics via `patchSchema` (../patch): an omitted threshold keeps its
  // administered value, exactly as the monitoring thresholds beside it behave.
  body: patchSchema(HandheldThresholds),
  summary: 'Configure the counter-fleet alert thresholds (OPS-029)',
  idempotent: true,
  response: HandheldThresholds,
});

// ---------------------------------------------------------------------------
// The fleet-wide device list (docs/plans/handheld-fleet.html 5, OPS-029)
// ---------------------------------------------------------------------------

/**
 * Where one counter stands right now, derived and never stored.
 *
 * Seven states, and the order below is the order they are decided in
 * (`fleetDeviceStatus`, handheld-fleet.ts) - which matters, because a device can
 * satisfy more than one and an operator wants the most actionable answer:
 *
 *  - `REMOVED` - the revoked row. It stays on the list whatever its last beat
 *    says, because "removed on Tuesday" is the answer to "why did the Sunmi
 *    stop", and the row IS the credential so its liveness is history.
 *  - `ASLEEP` - past the grace, but seen within the last twelve hours AND on
 *    charge at its last beat. A HEURISTIC and nothing more, because a sleeping
 *    device cannot report being asleep - it does not beat. It is the docked
 *    counter overnight, told apart from the counter somebody carried off.
 *  - `OFFLINE` - past the grace and not that, or never seen at all.
 *  - `BLOCKED` - a LIVE device the current offer blocks NOW: below the forced
 *    minimum with the grace window already passed. A blocked counter keeps
 *    heartbeating (plan 8), which is exactly what makes this list the evidence
 *    for a force nobody ordered.
 *  - `UPDATING` - a live device with a build staged, and that build is the
 *    latest published one; the next thing it does is ask a human to tap
 *    Install.
 *  - `CLOSED` - live, and its last beat came from the periodic job rather than
 *    from a screen (BAK-059): the machine is up and current, nobody has the
 *    app open. Its own state and not a flavour of ONLINE, because it is what a
 *    shut shop looks like at 22:00 and an operator reading the list at 06:20
 *    should be able to tell the two apart.
 *  - `ONLINE` - live from a screen, and none of those.
 *
 * LIVENESS IS DECIDED FIRST. A device that has been dark for a week is dark,
 * whatever offer the platform would compute for it if it ever came back -
 * printing BLOCKED for a counter nobody can reach sends an operator to the
 * force panel when the answer is a phone call.
 *
 * The live states are decided by the SAME grace window OPS-011 uses to call a
 * counter's heartbeat missing - `heartbeatMissing`, which reads the grace off
 * the last beat's own cadence (ten minutes from a screen, twenty from the
 * job). Deriving liveness a second way here is exactly how the dashboard and
 * the alert list came to disagree about one shop.
 */
export const FleetDeviceStatus = z.enum([
  'ONLINE',
  'CLOSED',
  'ASLEEP',
  'OFFLINE',
  'BLOCKED',
  'UPDATING',
  'REMOVED',
]);
export type FleetDeviceStatus = z.infer<typeof FleetDeviceStatus>;

/**
 * The one word about the COUNTER, as against the seven about a DEVICE above.
 *
 * `FleetDeviceStatus` answers "what is this unit doing" and is an operator's
 * tool for a rollout; this answers "may this shop be given an order, and does
 * it need a phone call", which is the question dispatch, the Admin dashboard,
 * the alert sweep and the partner's Devices dialog all ask. A shop can run a
 * Sunmi beside a browser tablet, so a counter is ready if ANY of its devices
 * is - which is why the two words are not the same word narrowed.
 *
 * Derived on every read and persisted nowhere: `counterReadinessOf()` in
 * `../projections/counter-readiness`, which is also where the five values, the
 * order they are decided in, and the schema itself are stated. This is the name
 * this mount's shapes already use for it, not a second definition - the schema
 * cannot live here, because `device-pairing` declares the field too and this
 * file imports THAT one.
 */
export const CounterReadiness = CounterReadinessSchema;
export type CounterReadiness = CounterReadinessValue;

/**
 * One paired device, wherever it is, with the shop and house it belongs to.
 *
 * `DeviceRow` beside it is the per-shop shape both mounts already share; this is
 * the same device seen from the platform rather than from one counter, so it
 * carries the join a fleet list needs and nothing else. The version an operator
 * filters on is `appVersion`, the stored string; the CODE is inside `telemetry`,
 * where the device put it, rather than a second column that could disagree with
 * the record it was read from.
 */
export const FleetDeviceRow = z.object({
  id: Uuid,
  label: z.string().min(1).max(60),
  /** Null until a device has reported what it is; see `DeviceRow.kind`. */
  kind: DeviceKind.nullable(),
  appVersion: z.string().max(40).nullable(),
  pairedAt: Instant.nullable(),
  lastSeenAt: Instant.nullable(),
  active: z.boolean(),
  revokedAt: Instant.nullable(),
  /** What the device calls itself - the system's device id on a Sunmi; see `DeviceRow.deviceId`. */
  deviceId: z.string().min(1).max(128),
  status: FleetDeviceStatus,
  /** What the device last said about itself (plan 9); see `DeviceRow.telemetry`. */
  telemetry: HandheldTelemetry.nullable(),
  telemetryAt: Instant.nullable(),
  /**
   * The shop this counter stands in, and the link the row's label opens. All
   * four are null for a handheld that is on the fleet and paired to no shop
   * (founder, 2026-09-15): it is still listed and still updated.
   */
  locationId: Uuid.nullable(),
  locationName: z.string().min(1).max(200).nullable(),
  /** The house above it - a chain's dark counter must name an address AND a company. */
  organizationId: Uuid.nullable(),
  organizationName: z.string().min(1).max(200).nullable(),
});
export type FleetDeviceRow = z.infer<typeof FleetDeviceRow>;

/**
 * Every device on the estate, in one list (OPS-029, plan 5).
 *
 * The screen that answers "which counters are still on 1.41" during a rollout.
 * Sorted with the devices an operator cannot yet explain at the top - never seen
 * at all, or paired in the last day - and everything else by last heartbeat,
 * newest first (plan 16.4: a rogue pairing should be a thing somebody sees).
 */
export const listFleetDevices = defineEndpoint({
  method: 'GET',
  path: '/internal/devices',
  mount: 'internal',
  summary: 'Every paired counter device across every bakery shop (OPS-029)',
  query: PageRequest.extend({
    /** Any of these statuses; omitted or empty means every status. Repeated on the wire. */
    status: z.array(FleetDeviceStatus).max(FleetDeviceStatus.options.length).optional(),
    /** Matches the device label, its shop's name or its house's name. */
    search: z.string().max(120).optional(),
    /** Any of these exact build names, as the devices last reported them. */
    versionName: z.array(z.string().max(40)).max(200).optional(),
  }),
  response: pageOf(FleetDeviceRow).extend({
    /**
     * Which statuses and builds the filters can offer, and how many devices
     * carry each (founder, 2026-09-14: an option no device carries is not
     * offered). Counted over the whole fleet the search matches - never one
     * page - each with the other filter applied and its own ignored, so ticking
     * 1.41 does not hide 1.42 from the panel. A device that has reported no
     * build counts under no version.
     */
    facets: z.object({
      status: z.array(FilterFacet),
      versionName: z.array(FilterFacet),
    }),
  }),
});

// ---------------------------------------------------------------------------
// One device: its page, its commands and its configuration
// (docs/plans/handheld-fleet.html 5, 11, OPS-029)
// ---------------------------------------------------------------------------

/** The device a page, a command or a config override is about. */
const DeviceParam = z.object({ deviceId: Uuid });

/**
 * One counter, read on its own page.
 *
 * The SAME row the fleet list is made of, deliberately: the page is where an
 * operator lands from that list, and a second shape for one device would be a
 * second derivation of its status - which is exactly how a list and a detail
 * screen come to disagree about whether a counter is online.
 */
export const getFleetDevice = defineEndpoint({
  method: 'GET',
  path: '/internal/devices/:deviceId',
  mount: 'internal',
  summary: 'One counter device, wherever it stands, with its current telemetry (OPS-029)',
  params: DeviceParam,
  response: FleetDeviceRow,
});

/**
 * Rename one counter (plan 5).
 *
 * The label is what EVERY screen identifies a device by - the fleet list, the
 * adoption panel's failed rows, an alert's payload, the shop's own Devices
 * modal - and for a long time the only thing that ever wrote one was the
 * device itself, at pairing, from a string it composed out of the day's date.
 * Two counters paired the same morning were then called the same thing for
 * ever, and nothing anywhere could tell them apart. Since 2026-08-29 the
 * platform numbers a new Sunmi (`Sunmi 00001`, `pairDevice`) and a re-pairing
 * keeps its row's name - this rename included.
 *
 * A PATCH of one field rather than a general device update: this is the only
 * field of a device row a human owns. Everything else on it - the kind, the
 * build, the liveness, the telemetry - is reported BY the device, and a desk
 * typing over any of those would be a fleet screen that lies.
 *
 * The reply is the whole row, as the device page's own read answers it, so the
 * screen writes the answer straight into that cache entry rather than asking
 * again for a record it was just handed.
 */
export const renameFleetDevice = defineEndpoint({
  method: 'PATCH',
  path: '/internal/devices/:deviceId',
  mount: 'internal',
  summary: 'Rename one counter device (OPS-029)',
  params: DeviceParam,
  /* Trimmed at the BOUNDARY, as the device message is: a label of four spaces
     satisfies `min(1)` and renders as an empty cell on every screen that
     identifies a counter by it. */
  body: z.object({ label: z.string().trim().min(1).max(60) }),
  idempotent: false,
  response: FleetDeviceRow,
});

/**
 * Retire one handheld from the fleet (founder, 2026-09-15).
 *
 * Removing a device from its shop no longer ends its life on the platform: a
 * Sunmi stays on the fleet, seen and updated, paired to nobody. This is what
 * does end it - the row is deleted, so its shop token and its fleet token are
 * both dead on the next call, and its builds stop at once (OPS-030). The
 * answer to a lost or stolen handheld. Pairing the machine again brings it
 * back as a new row.
 */
export const retireFleetDevice = defineEndpoint({
  method: 'DELETE',
  path: '/internal/devices/:deviceId',
  mount: 'internal',
  summary: 'Retire one counter device from the fleet; both its tokens die (OPS-030)',
  params: DeviceParam,
  idempotent: false,
  response: z.object({ retiredAt: Instant }),
});

/**
 * Issue one command to one counter (plan 11).
 *
 * It does not reach the device here: it is written down, and the device
 * collects it on its next beat. The reply is the RECORD, so the page can show
 * the command as issued-but-not-yet-delivered without asking for the list
 * again - which is also the honest state, and the one an operator watching a
 * dark counter needs to see.
 */
export const issueDeviceCommand = defineEndpoint({
  method: 'POST',
  path: '/internal/devices/:deviceId/commands',
  mount: 'internal',
  summary: 'Ask one counter to ring, print, resync, restart or show a message (OPS-029)',
  params: DeviceParam,
  body: z
    .object({
      kind: DeviceCommandKind,
      args: DeviceCommandArgs.optional(),
    })
    .refine((body) => (body.kind === 'SHOW_MESSAGE') === (body.args !== undefined), {
      message: 'SHOW_MESSAGE carries a text, and no other kind carries arguments',
      path: ['args'],
    }),
  idempotent: false,
  response: DeviceCommandRecord,
});

/**
 * What has been asked of this counter lately, newest first.
 *
 * Capped at the last fifty rather than paged: this is a panel on a page, not a
 * register, and the register is the audit log - every issue and every ack is a
 * row in it, under this device's own subject.
 */
export const listDeviceCommands = defineEndpoint({
  method: 'GET',
  path: '/internal/devices/:deviceId/commands',
  mount: 'internal',
  summary: 'The last fifty commands issued to one counter, and how each ended (OPS-029)',
  params: DeviceParam,
  response: z.object({ commands: z.array(DeviceCommandRecord).max(50) }),
});

/**
 * What this counter has left behind in the FLEET bucket (plan 13).
 *
 * A listing of OBJECTS, read straight from the bucket under this device's own
 * prefix - there is no index row anywhere, deliberately: the objects are the
 * record and the retention sweep deletes them, so a table beside them would be
 * a second list to keep in step with a bucket that empties itself.
 */
export const listDeviceDiagnostics = defineEndpoint({
  method: 'GET',
  path: '/internal/devices/:deviceId/diagnostics',
  mount: 'internal',
  summary: 'The crash reports and diagnostic bundles one counter has uploaded (OPS-029)',
  params: DeviceParam,
  response: z.object({ items: z.array(DiagnosticObject).max(500) }),
});

/**
 * One of those objects, streamed against the operator's own session.
 *
 * The path carries the NAME and never the key: the worker composes
 * `diagnostics/<device row id>/<name>` itself from the device it just resolved,
 * so a name is powerless to address another device's object however it is
 * spelled - and `DiagnosticObjectName` refuses anything but the exact shape the
 * uploader writes. A key built by concatenating a path segment is how a route
 * reads objects it was never meant to (plan 16.4), and this is the second route
 * in this platform that reads that bucket.
 */
export const downloadDeviceDiagnostic = defineEndpoint({
  method: 'GET',
  path: '/internal/devices/:deviceId/diagnostics/:key',
  mount: 'internal',
  summary: 'One diagnostic document, as a download (OPS-029)',
  params: DeviceParam.extend({ key: DiagnosticObjectName }),
  /* Streamed JSON with a `content-disposition`, so there is no shape the typed
     client could parse - the same declaration the APK download carries. */
  response: z.never(),
});

/**
 * The three configuration layers (plan 11), each behind the same pair of
 * endpoints: read what this layer overrides and what a device under it resolves
 * to, and PATCH the override.
 *
 * Three pairs rather than one taking a scope, because the three are addressed
 * by three things this API already addresses - the platform, a shop, a device
 * row - and a `{ scope, id }` body would put the routing inside the payload,
 * where no path can express it and no permission can read it.
 */
export const getHandheldConfig = defineEndpoint({
  method: 'GET',
  path: '/internal/handheld/config',
  mount: 'internal',
  summary: 'The platform-wide counter device configuration (OPS-029)',
  response: HandheldConfigLayer,
});

export const setHandheldConfig = defineEndpoint({
  method: 'PUT',
  path: '/internal/handheld/config',
  mount: 'internal',
  // PATCH semantics via `patchSchema` (../patch), with the third state doing
  // real work here: `null` REMOVES an override rather than clearing a value.
  body: HandheldConfigPatch,
  summary: 'Configure the counter devices platform-wide (OPS-029)',
  idempotent: true,
  response: HandheldConfigLayer,
});

export const getLocationHandheldConfig = defineEndpoint({
  method: 'GET',
  path: '/internal/bakery-locations/:locationId/handheld-config',
  mount: 'internal',
  summary: 'One shop counter-device configuration, and what it resolves to (OPS-029)',
  params: z.object({ locationId: Uuid }),
  response: HandheldConfigLayer,
});

export const setLocationHandheldConfig = defineEndpoint({
  method: 'PUT',
  path: '/internal/bakery-locations/:locationId/handheld-config',
  mount: 'internal',
  summary: 'Override the counter-device configuration for one shop (OPS-029)',
  params: z.object({ locationId: Uuid }),
  body: HandheldConfigPatch,
  idempotent: true,
  response: HandheldConfigLayer,
});

export const getDeviceHandheldConfig = defineEndpoint({
  method: 'GET',
  path: '/internal/devices/:deviceId/handheld-config',
  mount: 'internal',
  summary: 'One counter own configuration override, and what it resolves to (OPS-029)',
  params: DeviceParam,
  response: HandheldConfigLayer,
});

export const setDeviceHandheldConfig = defineEndpoint({
  method: 'PUT',
  path: '/internal/devices/:deviceId/handheld-config',
  mount: 'internal',
  summary: 'Override the configuration of one counter device (OPS-029)',
  params: DeviceParam,
  body: HandheldConfigPatch,
  idempotent: true,
  response: HandheldConfigLayer,
});
